import { studioTool, type PublicationDto } from '@ads/shared';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { dateLong, dateTime } from '../lib/format';
import { useArtifact, usePublications, useSession } from '../lib/queries';
import { useArtifactChat } from '../lib/useArtifactChat';
import { PublishSheet } from '../site/Publish';
import { SiteCanvas } from '../site/SiteCanvas';
import { ChatPanel } from '../studio/ChatPanel';
import { PaperStage } from '../studio/PaperStage';

/* Uma peça, renderizada com o design system da versão que a gerou — e a
   conversa com o agente ao lado, para continuar melhorando. */
export function Peca() {
  const { id } = useParams();
  const nav = useNavigate();
  const { me } = useSession();
  const q = useArtifact(id);
  const a = q.data;
  const t = a ? studioTool(a.tool) : null;
  const chat = useArtifactChat(id);
  const blocks = chat.blocks ?? a?.body ?? [];
  const units = a ? (a.outline?.sections.length ?? 0) || blocks.filter(b => b.type === (a.tool === 'site' ? 'page' : 'section')).length || blocks.length : 0;
  useViewMeta({ nav: 'biblioteca', crumb: ['Biblioteca', t?.name ?? 'Peça'], j: .9 });
  const isPage = t?.output === 'page';
  const pubs = usePublications(isPage ? a?.id : null);
  const live = pubs.data?.find(p => p.status === 'done' && p.url);
  const [publishing, setPublishing] = useState(false);
  /* veio da Biblioteca pelo "Editar com IA": abre direto na conversa */
  const [params, setParams] = useSearchParams();
  const [focusKey, setFocusKey] = useState(() => (params.has('conversa') ? 1 : 0));
  useEffect(() => { if (params.has('conversa')) setParams({}, { replace: true }); }, [params, setParams]);
  /* o site no ar ficou para trás: a conversa criou versões depois da publicação */
  const behind = !!a && !!live && live.revision != null && a.revision > live.revision;

  return (
    <section className="view on" data-v="peca">
      <button className="back" type="button" onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav('/biblioteca'))}><Ic.ChevronLeft />Voltar</button>
      {q.isError && <div className="notice"><Ic.Warning /><p>Esta peça não existe ou você não tem acesso a ela.</p></div>}
      {a && t && (
        <>
          <div className="peca-head">
            <div>
              <div className="eyebrow"><span className="rule" /><span>{t.name}</span><b>{a.status === 'ready' ? 'Pronta' : a.status === 'failed' ? 'Falhou' : 'Gerando'}</b></div>
              <p className="peca-meta">
                Gerada em {dateTime(a.createdAt)}{a.createdBy ? ` por ${a.createdBy}` : ''} · {blocks.length} blocos{a.revision > 1 ? ` · versão ${a.revision}` : ''}
                {a.designSystem ? ` · ${a.designSystem.name} ${a.designSystem.version}` : ''}
                {a.provider === 'mock' ? ' · roteiro de demonstração' : a.model ? ` · ${a.model}` : ''}
              </p>
            </div>
            {a.status === 'ready' && me.org.role !== 'viewer' && (
              <button className="btn-ghost peca-ai" type="button" onClick={() => setFocusKey(k => k + 1)}>
                <span className="with-ic"><Ic.Sparkle />Editar com IA</span>
              </button>
            )}
          </div>
          {a.status === 'failed' && <div className="notice warn"><Ic.Warning /><p>{a.error}</p></div>}
          {behind && live && (
            <div className="notice pub-behind">
              <Ic.Rocket />
              <p>O site no ar ({live.url!.replace(/^https?:\/\//, '').replace(/\/$/, '')}) está na <b className="strong">versão {live.revision}</b>. A peça já está na versão {a.revision}: as alterações da conversa vão para o ar quando você publicar de novo.</p>
              <button className="btn-ghost" type="button" disabled={chat.busy} onClick={() => setPublishing(true)}>Publicar de novo</button>
            </div>
          )}
          <div className={`peca-edit${behind ? ' with-note' : ''}`}>
            <ChatPanel meta={t} chat={chat} units={units} focusKey={focusKey} />
            {isPage ? (
              <div className="peca-site gen-stage site">
                <SiteCanvas title={chat.title ?? a.title} orgName={a.org.name || me.org.name} blocks={blocks} ds={a.designSystem}
                  primaryLink={a.briefing.link} running={a.status === 'generating' || !!chat.live?.reply} fresh={chat.fresh} settleTop={false}
                  host={live?.url ? live.url.replace(/^https?:\/\//, '').replace(/\/$/, '') : null}
                  openHref={a.status === 'ready' && !chat.busy ? `/api/artifacts/${a.id}/site/` : null} />
              </div>
            ) : (
              <div className="peca-paper">
                <PaperStage title={chat.title ?? a.title} org={a.org.name || me.org.name} deliverable={t.deliverable}
                  date={dateLong(a.createdAt)} blocks={blocks} ds={a.designSystem} liveFrom={Infinity} fresh={chat.fresh}
                  writing={a.status === 'generating'} />
              </div>
            )}
          </div>
          {isPage && !!pubs.data?.length && <PubHistory list={pubs.data} />}
        </>
      )}

      <Actions>
        {!isPage && <button className="btn-ghost" type="button" onClick={() => window.print()}><span className="with-ic"><Ic.Print />Imprimir ou salvar PDF</span></button>}
        {t && isPage && <button className="btn-ghost" type="button" onClick={() => nav(`/studio/${t.key}`)}>Nova versão</button>}
        {t && isPage && a?.status === 'ready' && (
          <button className="btn-primary" type="button" disabled={chat.busy} title={chat.busy ? 'Espere o agente terminar a alteração' : undefined} onClick={() => setPublishing(true)}>
            <span className="with-ic"><Ic.Rocket />{live ? 'Publicar de novo' : 'Aprovar e publicar'}</span>
          </button>
        )}
        {t && !isPage && <button className="btn-primary" type="button" onClick={() => nav(`/studio/${t.key}`)}>Nova versão<Ic.ArrowRight /></button>}
      </Actions>
      {publishing && a && t && <PublishSheet artifactId={a.id} title={chat.title ?? a.title} tool={t.key} onClose={() => setPublishing(false)} />}
    </section>
  );
}

function PubHistory({ list }: { list: PublicationDto[] }) {
  return (
    <div className="pub-hist">
      <div className="fam-h sm">Publicações</div>
      <ul>
        {list.slice(0, 6).map(p => (
          <li key={p.id}>
            <em data-s={p.status}>{p.status === 'done' ? (p.url ? 'no ar' : 'versionada') : p.status === 'failed' ? 'falhou' : 'publicando'}</em>
            <span className="ph-slug">{p.slug}{p.revision ? <i className="ph-v">v{p.revision}</i> : null}</span>
            <span className="ph-when">{dateTime(p.createdAt)}{p.createdBy ? ` · ${p.createdBy}` : ''}</span>
            <span className="ph-act">
              {p.url && p.status === 'done' && <a href={p.url} target="_blank" rel="noopener"><Ic.External />Site</a>}
              {p.commitUrl && <a href={p.commitUrl} target="_blank" rel="noopener"><Ic.Branch />{p.commitSha?.slice(0, 7)}</a>}
            </span>
            {p.status === 'failed' && p.error && <span className="ph-err">{p.error}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
